import { useEffect, useRef } from "react";

type AdSize =
  | "leaderboard"      // 728x90  desktop top/bottom
  | "mobile-banner"    // 320x50  mobile top/bottom
  | "rectangle"        // 300x250 sidebar
  | "large-rectangle"  // 336x280 in-content
  | "responsive"       // full-width responsive
  | "sticky-mobile";   // 320x50  sticky bottom mobile

interface AdSlotProps {
  size: AdSize;
  className?: string;
  /** Google AdSense data-ad-slot ID – fill in when going live */
  adSlot?: string;
  /** Google AdSense publisher ID – fill in when going live */
  adClient?: string;
}

const sizeMap: Record<AdSize, { w: number | "100%"; h: number; label: string }> = {
  leaderboard:      { w: 728,   h: 90,  label: "728 × 90"  },
  "mobile-banner":  { w: 320,   h: 50,  label: "320 × 50"  },
  rectangle:        { w: 300,   h: 250, label: "300 × 250" },
  "large-rectangle":{ w: 336,   h: 280, label: "336 × 280" },
  responsive:       { w: "100%",h: 90,  label: "Responsive" },
  "sticky-mobile":  { w: 320,   h: 50,  label: "320 × 50"  },
};

export default function AdSlot({ size, className = "", adSlot, adClient }: AdSlotProps) {
  const insRef = useRef<HTMLModElement>(null);
  const { w, h, label } = sizeMap[size];

  useEffect(() => {
    if (adClient && adSlot) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ((window as any).adsbygoogle = (window as any).adsbygoogle || []).push({});
      } catch (_) {
        // AdSense not loaded yet
      }
    }
  }, [adClient, adSlot]);

  const widthStyle = w === "100%" ? "100%" : `${w}px`;
  const heightStyle = `${h}px`;

  // When real AdSense credentials provided, render the real ins tag
  if (adClient && adSlot) {
    return (
      <div className={`flex items-center justify-center ${className}`}>
        <ins
          ref={insRef}
          className="adsbygoogle"
          style={{ display: "block", width: widthStyle, height: heightStyle }}
          data-ad-client={adClient}
          data-ad-slot={adSlot}
          data-ad-format={size === "responsive" ? "auto" : undefined}
          data-full-width-responsive={size === "responsive" ? "true" : undefined}
        />
      </div>
    );
  }

  // Placeholder shown before AdSense goes live — hidden until credentials are set
  return null;
}
